import React from 'react'
import { Box, Flex, Label, Text } from '@workwhile/ui'
import { formatDistanceToNowStrict } from 'date-fns'
import { CopyableCode } from 'components/Shared/CopyableCode'
import { CopyableLink } from 'components/Shared/CopyableLink'
import { TimeConversionPopover } from 'components/Shared/TimeConversion'
import { getLocalTimezone } from 'lib/time'
import { HighnoteTransferAuditEvent } from 'api/payments/getUserPayments'
import { AuditEventMetadataTooltip } from './AuditEventMetadataTooltip'
import { formatPaymentTimestamp } from './formatPaymentTimestamp'
import { ProviderDetailLine } from './ProviderDetailLine'
import { getAuditActionLabel } from '../../utils/labels'

type HighnoteAuditTimelineProps = {
  auditEvents: HighnoteTransferAuditEvent[]
  targetTimezone: string
}

export const HighnoteAuditTimeline = ({
  auditEvents,
  targetTimezone,
}: HighnoteAuditTimelineProps) => {
  if (auditEvents.length === 0) {
    return (
      <Text fontSize={2} color="lightText">
        No audit events recorded for this transfer.
      </Text>
    )
  }

  return (
    <Flex flexDirection="column" gap={3}>
      {auditEvents.map((event, index) => (
        <Box
          key={event.id}
          pb={index < auditEvents.length - 1 ? 3 : 0}
          borderBottom={
            index < auditEvents.length - 1 ? '1px solid' : undefined
          }
          borderColor={
            index < auditEvents.length - 1 ? 'neutrals.100' : undefined
          }
        >
          <Flex
            justifyContent="space-between"
            alignItems="flex-start"
            mb={1}
            gap={2}
          >
            <Text fontSize={2} fontWeight="bold" lineHeight="1.4">
              {getAuditActionLabel(event.action)}
              {' · '}
              <Text as="span" fontSize={1} fontWeight="normal">
                Event ID: <CopyableCode value={String(event.id)} />
              </Text>
            </Text>
            <Label variant={event.succeeded ? 'success' : 'error'} size="small">
              {event.succeeded ? 'Succeeded' : 'Failed'}
            </Label>
          </Flex>

          <Flex flexDirection="column" gap={1}>
            <Text fontSize={1} color="lightText">
              <TimeConversionPopover
                value={event.originatedAt}
                zones={[
                  { timeZone: getLocalTimezone(), context: 'Your computer' },
                  { timeZone: targetTimezone, context: 'Worker' },
                  { timeZone: 'UTC' },
                ]}
                primaryTimeZone={targetTimezone}
                side="top"
              >
                <Text as="span" color="lightText" fontSize={1}>
                  {formatPaymentTimestamp(event.originatedAt, targetTimezone)}
                </Text>
              </TimeConversionPopover>
              {' · '}
              {formatDistanceToNowStrict(new Date(event.originatedAt), {
                addSuffix: true,
              })}
            </Text>

            {event.actorType === 'user' ? (
              <Text
                fontSize={1}
                color="lightText"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                User
                <AuditEventMetadataTooltip event={event} />:{' '}
                {event.actorName ?? 'Unknown'}
                {event.actorId ? (
                  <>
                    {' ('}
                    <CopyableLink
                      label={String(event.actorId)}
                      link={`/users/${event.actorId}`}
                    />
                    {')'}
                  </>
                ) : null}
              </Text>
            ) : (
              <ProviderDetailLine label="Webhook ID">
                {event.webhookEventId != null ? (
                  <CopyableCode value={String(event.webhookEventId)} />
                ) : (
                  'Unknown'
                )}
              </ProviderDetailLine>
            )}

            {event.highnoteErrorCode ? (
              <Text fontSize={1} color="error">
                Error code {event.highnoteErrorCode}
              </Text>
            ) : null}

            {event.highnoteRequestId && event.actorType !== 'user' ? (
              <ProviderDetailLine label="Request ID">
                <CopyableCode value={event.highnoteRequestId} />
              </ProviderDetailLine>
            ) : null}
          </Flex>
        </Box>
      ))}
    </Flex>
  )
}
